import { useLayoutEffect, useRef, useState } from 'react';

function columnsThatFit (containerWidth: number, minColumnWidth: number, gap: number) {
  const spacePerColumn = minColumnWidth + gap;
  const lastColumnHasNoTrailingGap = gap;
  const columns = Math.floor((containerWidth + lastColumnHasNoTrailingGap) / spacePerColumn);
  return Math.max(1, columns);
}

/**
 * @param {number} minColumnWidth narrowest a column may be, in px
 * @param {number} gap column gap, in px
 * @param {number} initialColumns count to use before the grid is first measured
 * @returns {[React.RefObject<HTMLDivElement>, number]} ref to attach to the grid, and its column count
 */
export default function useGridColumns (minColumnWidth: number, gap: number, initialColumns: number) {
  const gridRef = useRef<HTMLDivElement>(null);
  const [columns, setColumns] = useState(initialColumns);

  useLayoutEffect(() => {
    const grid = gridRef.current;
    const canObserveResize = typeof ResizeObserver !== 'undefined';
    if (!grid || !canObserveResize) return undefined;

    const observer = new ResizeObserver(([entry]) => {
      const gridWidth = entry.contentRect.width;
      const isHidden = gridWidth === 0;
      if (isHidden) return;
      setColumns(columnsThatFit(gridWidth, minColumnWidth, gap));
    });
    observer.observe(grid);

    return () => observer.disconnect();
  }, [minColumnWidth, gap]);

  return [gridRef, columns] as const;
}
